import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProcurementProcedure from '../components/ProcurementProcedure';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ShieldCheck, FileText, Ship, Banknote, Globe, ArrowRight, FlaskConical, CheckCircle2 } from 'lucide-react';
import { API } from '../App';

export default function OilAndGasPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/products`, { params: { category: 'Oil & Gas' } });
        if (!active) return;
        setProducts((r.data?.products || r.data || []).slice(0, 6));
      } catch (_e) {
        if (active) setProducts([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="oil-gas-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center opacity-25"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1518553552028-7a1041ac72b8?w=2000&q=80')",
          }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-background via-background/95 to-background/70" />
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <span
            className="text-xs font-bold uppercase tracking-[0.2em] text-primary bg-primary/10 px-4 py-2 rounded-full inline-block mb-6"
            data-testid="oilgas-eyebrow"
          >
            B2B Commodity Procurement
          </span>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-foreground mb-6 max-w-3xl"
            data-testid="oilgas-title"
          >
            Faith-led Oil &amp; Gas trading. Real cargoes. Real contracts.
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mb-8">
            Crude Oil, EN590 (Diesel) ULSD 10ppm, and Jet A1 — sourced through
            vetted Christian-led suppliers, transacted under the standard
            LOI → ICPO → SPA workflow with SGS inspection and SBLC / MT103 / DLC
            settlement.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/products?category=Oil%20%26%20Gas">
              <Button size="lg" data-testid="oilgas-cta-products">
                View available commodities <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
            <a href="mailto:procurement@christkingdomglobalmarketplace.com?subject=LOI%20Inquiry">
              <Button size="lg" variant="outline" data-testid="oilgas-cta-loi">
                <FileText className="h-5 w-5 mr-2" /> Submit an LOI
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* TRUST STRIPE */}
      <section className="border-y border-border/60 bg-muted/30">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, title: 'SGS / Intertek inspection', body: 'Every cargo independently verified at loading port.' },
            { icon: Banknote, title: 'Bank-instrument settlement', body: 'SBLC, MT103, DLC — never escrow-only.' },
            { icon: Ship, title: 'CIF, FOB, TTO/TTT', body: 'We work with the Incoterm that fits your operation.' },
            { icon: Globe, title: 'Global delivery', body: 'Rotterdam, Houston, Fujairah, Singapore, Lagos.' },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="flex gap-3" data-testid={`oilgas-trust-${s.title.replace(/\s+/g, '-').toLowerCase()}`}>
                <Icon className="h-6 w-6 text-primary shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-sm">{s.title}</div>
                  <div className="text-xs text-muted-foreground leading-relaxed mt-1">{s.body}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* COMMODITIES */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading font-medium" data-testid="oilgas-commodities-heading">
              Live commodity offers
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              Prices are indicative. All deals close under a signed SPA.
            </p>
          </div>
          <Link to="/products?category=Oil%20%26%20Gas" className="text-sm text-primary hover:underline flex items-center gap-1">
            See all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {['s1', 's2', 's3'].map((k) => (
              <div key={k} className="h-64 bg-muted/40 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <FlaskConical className="h-10 w-10 mx-auto mb-3 text-primary" />
              <p className="mb-4">No live commodity listings at the moment.</p>
              <a href="mailto:procurement@christkingdomglobalmarketplace.com">
                <Button variant="outline" size="sm">Contact procurement team</Button>
              </a>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <Link
                key={p.product_id}
                to={`/products/${p.product_id}`}
                className="group rounded-2xl border border-border bg-background hover:border-primary/40 hover:shadow-md transition overflow-hidden"
                data-testid={`oilgas-commodity-${p.product_id}`}
              >
                <div className="aspect-video bg-muted overflow-hidden">
                  <img
                    src={(p.images && p.images[0]) || 'https://images.unsplash.com/photo-1518553552028-7a1041ac72b8?w=900&q=80'}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-wide text-primary mb-1">
                    {p.subcategory || 'Commodity'}
                  </div>
                  <h3 className="font-medium line-clamp-1">{p.name}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                    {p.description || 'Request specification sheet & current pricing.'}
                  </p>
                  <div className="mt-4 text-sm text-primary flex items-center gap-1">
                    Request quote <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* PROCEDURE */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-16">
        <ProcurementProcedure />
      </section>

      {/* WHO IT&rsquo;S FOR */}
      <section className="bg-muted/30 border-y border-border/60">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16">
          <h2 className="text-2xl md:text-3xl font-heading font-medium mb-8" data-testid="oilgas-buyers-heading">
            Who we work with
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Refineries & blenders', body: 'Direct allocation buyers needing consistent, spec-grade feedstock.' },
              { title: 'National importers', body: 'Government and licensed importers in West Africa, Asia & LATAM.' },
              { title: 'Trading houses', body: 'Mid-tier traders looking for additional contracted volume.' },
            ].map((c) => (
              <div key={c.title} className="bg-background rounded-2xl border border-border p-6">
                <CheckCircle2 className="h-6 w-6 text-primary mb-3" />
                <h3 className="font-medium mb-2">{c.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-foreground text-background">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-medium mb-4" data-testid="oilgas-final-cta-heading">
            Ready to open a commercial conversation?
          </h2>
          <p className="text-background/70 max-w-2xl mx-auto mb-8">
            Submit a Letter of Intent with your target product, volume,
            destination port, and delivery window. We&rsquo;ll respond with a
            Soft Corporate Offer within 72 hours.
          </p>
          <a href="mailto:procurement@christkingdomglobalmarketplace.com?subject=LOI%20Inquiry">
            <Button size="lg" data-testid="oilgas-final-cta">
              <FileText className="h-5 w-5 mr-2" /> Submit your LOI
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
